import { Dispatch, useCallback, useContext, useEffect, useReducer, useState } from "react";
import {
  JobInfo,
  JobStates,
  newHierarchyNode,
  trackModes,
  trackSettingsList,
  ViewerInitState,
  viewerLogType,
  viewerMapping,
  viewerStateType,
  viewerType,
  viewPortType,
} from "./ViewerLayoutTypes";
import { ActionType, ViewerStateReducer } from "./ViewerStateReducer";
import { initParameterList, initViewerType, needUpdate, updateCopy } from "./ViewerLayoutUtils";
import { initViewerModeType, trackState, viewerModeType } from "./ViewerTypes";
import { checkRunningJobs, fetchDatasets, fetchParserLog, fetchParserOutput, reparseDatasets } from "./ViewerLayoutApi";
import { SessionContext } from "../common/contexts/SessionContext";
import produce from "immer";

export type FullViewerProps = {
  ids: number[];
};

export type ViewerInteractionSettings = {
  showNavigation: boolean;
  interactiveMode: boolean;
};

export type globalViewerState = {
  ids: number[];
  state: viewerStateType;
  viewerLogs: viewerLogType[];
  parserOutput: string;
  viewerVisualState: Record<string, ViewerInitState>;
  viewerInteractionSettings: ViewerInteractionSettings;
  reparsing: boolean;
  dispatch: Dispatch<ActionType>;
  setIds: (ids: number[], isMounted: () => boolean) => void;
  changeTrackSettings: (list: Partial<trackSettingsList>) => void;
  setViewerMode: (viewerMode: viewerModeType) => void;
  setViewPort: (viewPort: viewPortType) => void;
  setTracksToZoom: (ids: string[]) => void;
  changeTrackState: (states: { id: string; state: trackState }[]) => void;
  setViewerVisualState: (activeViewer: viewerType, newState: ViewerInitState) => void;
  setViewerInteractionSettings: (settings: ViewerInteractionSettings) => void;
  reparseCurrentDatasets: (onReparseDone?: () => void) => void;
  fetchCurrentParserOutputs: (onLoad: () => void) => void;
  fetchCurrentParserLogs: (onLoad: () => void) => void;
  setActiveViewer: (activeViewer: viewerType) => void;
  setSelectedTracks: (ids: string[]) => void;
  forceReload: () => void;
};

export const useViewerState = (initIds?: number[], initState?: Partial<viewerStateType>): globalViewerState => {
  const [ids, setIds] = useState<number[]>([]);
  const [reparsing, setReparsing] = useState<boolean>(false);
  const [viewerLogs, setViewerLogs] = useState<viewerLogType[]>([]);
  const [parserOutput, setParserOutput] = useState<string>("");
  const [viewerVisualState, setViewerVisualState] = useState<Record<string, ViewerInitState>>({});
  const [viewerInteractionSettings, setViewerInteractionSettings] = useState<ViewerInteractionSettings>({
    showNavigation: true,
    interactiveMode: true,
  });

  const { api } = useContext(SessionContext);

  const state: viewerStateType = {
    count: 0,
    datasets: {},
    parserLogs: [],
    tracks: {},
    tracksHierarchy: newHierarchyNode(),
    viewerTracks: {},
    datatracks: {},
    annotations: [],
    parameter: initParameterList(),
    internalParameters: [],
    updateParameter: false,
    tracksToZoom: [],
    selectedTracks: [],
    viewerMode: initViewerModeType(),
    viewerTypes: initViewerType(),
    viewerCalls: { xy: 0, xyz: 0, image: 0, pdf: 0, table: 0, molecule: 0, unknown: 0 },
    activeViewer: undefined,
    viewerSettings: {},
    noTracks: undefined,
    noParameters: undefined,
    viewPort: { width: 0, height: 0, left: 0, top: 0 },
    viewerStatus: { status: "success", logs: [] },
    ...initState,
  };

  const [viewerState, dispatch] = useReducer<typeof ViewerStateReducer>(ViewerStateReducer, state);

  const setTracksToZoom = useCallback((ids: string[]) => dispatch({ type: "setTracksToZoom", ids: ids }), []);

  useEffect(() => {
    if (state.activeViewer && !(state.activeViewer in viewerVisualState) && setTracksToZoom)
      setTracksToZoom(
        Object.keys(state.tracks).filter((id) => state.tracks[id].settings.visible && state.tracks[id].settings.active)
      );
  }, [state.activeViewer, setTracksToZoom, state.tracks, viewerVisualState]); // SK: caution this useEffect was formally not dependent of tracks and viewerState

  const updateViewerVisualState = useCallback(
    (activeViewer: viewerType, newState: ViewerInitState) => {
      if (!activeViewer) return;

      const getState = (stateRecord: Record<string, ViewerInitState>, active: viewerType, state: ViewerInitState) => {
        const s = Object.assign({}, stateRecord);
        s[active] = state;
        return s;
      };

      if (activeViewer in viewerVisualState) {
        if (needUpdate(viewerVisualState[activeViewer], newState))
          setViewerVisualState(getState(viewerVisualState, activeViewer, newState));
      } else {
        setViewerVisualState(getState(viewerVisualState, activeViewer, newState));
      }
    },
    [viewerVisualState]
  );

  const changeDatasetIds = useCallback(
    (newIds: number[], isMounted: () => boolean) => {
      const [copy, doUpdate] = updateCopy(ids, newIds);

      if (doUpdate) {
        setIds(copy);
        if (copy.length > 0 || (reparsing === false && copy.length > 0)) {
          console.log(`--- datasets ${copy} ---`);
          fetchDatasets(api, copy, dispatch, viewerState.count, isMounted).then((count) => {
            if (isMounted()) dispatch({ type: "incrementCount", count: count });
          });
        }
      }
    },
    // log cleanup
    // TODO: check dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ids]
  );

  var forceReload = useCallback(() => {
    fetchDatasets(api, ids, dispatch, viewerState.count, () => true).then((count) => {
      dispatch({ type: "incrementCount", count: count });
    });
    // log cleanup
    // TODO: check dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  useEffect(() => {
    if (initIds !== undefined) changeDatasetIds(initIds, () => true);
    // log cleanup
    // TODO: check dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initIds]);

  useEffect(() => {
    let activeViewer: viewerType | undefined = viewerState.activeViewer;
    if (
      viewerState.activeViewer === undefined ||
      Object.values(viewerState.tracks).every((t) => t.viewer !== activeViewer)
    ) {
      if (Object.keys(viewerState.tracks).length > 0) {
        const id = Object.values(viewerState.tracks)
          .filter((t) => t.viewer)
          .sort((a, b) => a.index - b.index)[0].id;
        activeViewer = viewerState.tracks[id].viewer;
        // ########### DELETE THIS: Just for testing ################
        // activeViewer = "sequence";
      }
    }
    dispatch({ type: "setActiveViewer", viewerType: activeViewer });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerState.tracks]);

  const changeTrackSettings = useCallback(
    (list: Partial<trackSettingsList>) =>
      dispatch({
        type: "setMultipleTrackSettings",
        trackSettingsList: list,
      }),
    []
  );

  const setActiveViewer = useCallback(
    (activeViewer: viewerType) => dispatch({ type: "setActiveViewer", viewerType: activeViewer }),
    // log cleanup
    // TODO: check dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewerState.activeViewer]
  );

  useEffect(() => {
    const viewer = viewerMapping[viewerState.activeViewer ?? "unknown"];
    let trackMode = trackModes.multiVisible;
    switch (viewer) {
      case "SpectrumComponent":
        trackMode = trackModes.multiVisible;
        break;
      case "ImageComponent":
        trackMode = trackModes.multiVisible;
        break;
      case "MoleculeComponent":
        trackMode = trackModes.singleVisible;
        break;
      case "PDFComponent":
        trackMode = trackModes.singleVisible;
        break;
      case "TableComponent":
        trackMode = trackModes.singleVisible;
        break;
    }

    dispatch({
      type: "setViewerMode",
      viewerMode: produce(viewerState.viewerMode, (next) => {
        next.trackMode = trackMode;
      }),
    });
    // log cleanup
    // TODO: check dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewerState.activeViewer]);

  // const setTrackMode = useCallback(
  //   (trackMode: trackModes) => {
  //     // if (viewerState.viewerMode.trackMode !== trackMode)
  //     setViewerMode(
  //       produce(viewerState.viewerMode, (next) => {
  //         next.trackMode = trackMode;
  //       })
  //     );
  //   },
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  //   [viewerState.viewerMode.trackMode]
  // );

  const changeTrackState = useCallback(
    (states: { id: string; state: trackState }[]) =>
      dispatch({
        type: "setMultipleTrackStates",
        states: states,
      }),
    []
  );

  const setViewerMode = useCallback(
    (viewerMode: viewerModeType) =>
      dispatch({
        type: "setViewerMode",
        viewerMode: viewerMode,
      }),
    []
  );

  const setViewPort = useCallback(
    (viewPort: viewPortType) => dispatch({ type: "setViewPort", viewPort: viewPort }),
    []
  );

  const setSelectedTracks = useCallback((ids: string[]) => {
    // console.log("ids", ids);
    dispatch({ type: "setSelectedTracks", ids: ids });
  }, []);

  const reparseDone = useCallback(
    async (jobs: JobInfo[], onReparseDone?: () => void) => {
      jobs = await checkRunningJobs(api, jobs);
      if (jobs.some((job) => job.state !== JobStates.finished)) setTimeout(reparseDone, 500, jobs, onReparseDone);
      else {
        setReparsing(false);
        if (onReparseDone) onReparseDone();
      }
    },
    [api]
  );

  const reparseCurrentDatasets = useCallback(
    async (onReparseDone?: () => void) => {
      setReparsing(true);
      const jobs = await reparseDatasets(api, ids);
      reparseDone(jobs, onReparseDone);
    },
    // log cleanup
    // TODO: check dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, ids]
  );

  const fetchCurrentParserLogs = useCallback(
    async (onLoad: () => void) => {
      const logs = await fetchParserLog(api, ids);
      setViewerLogs(logs);
      onLoad();
    },
    [api, ids]
  );

  const fetchCurrentParserOutputs = useCallback(
    async (onLoad: () => void) => {
      const outputs = await fetchParserOutput(api, ids);
      setParserOutput(outputs.join("\n------\n"));
      onLoad();
    },
    [api, ids]
  );

  useEffect(() => {
    // console.log("update parameter");
    if (viewerState.updateParameter) dispatch({ type: "updateParameter" });
  }, [viewerState.updateParameter]);

  return {
    ids: ids,
    state: viewerState,
    viewerLogs: viewerLogs,
    parserOutput: parserOutput,
    viewerVisualState: viewerVisualState,
    viewerInteractionSettings: viewerInteractionSettings,
    reparsing: reparsing,
    dispatch: dispatch,
    setIds: changeDatasetIds,
    changeTrackSettings: changeTrackSettings,
    setViewerMode: setViewerMode,
    setViewPort: setViewPort,
    setTracksToZoom: setTracksToZoom,
    changeTrackState: changeTrackState,
    setViewerVisualState: updateViewerVisualState,
    setViewerInteractionSettings: setViewerInteractionSettings,
    reparseCurrentDatasets: reparseCurrentDatasets,
    fetchCurrentParserOutputs: fetchCurrentParserOutputs,
    fetchCurrentParserLogs: fetchCurrentParserLogs,
    setActiveViewer: setActiveViewer,
    setSelectedTracks: setSelectedTracks,
    forceReload: forceReload,
  };
};
